/* VICIO SELLERS · motion layer. Vanilla ports (no React, no deps) of React Bits effects:
   Silk, ScrollVelocity, BlurText, TiltedCard + GlareHover, SpotlightCard, Magnet, StarBorder, ClickSpark.
   React Bits — Copyright (c) David Haz — MIT + Commons Clause (https://github.com/DavidHDev/react-bits). */
(() => {
  const d = document, W = window;
  const fine = matchMedia('(hover:hover) and (pointer:fine)').matches;
  const small = matchMedia('(max-width:760px)').matches;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) return;
  const lerp = (a, b, t) => a + (b - a) * t;
  const onView = (el, cb, margin) => {
    if (!('IntersectionObserver' in W)) return cb(true);
    new IntersectionObserver((es) => es.forEach((e) => cb(e.isIntersecting)), { rootMargin: margin || '0px' }).observe(el);
  };

  /* ---------- Silk (hero background, raw WebGL) ---------- */
  function silk() {
    if (small || !fine) return;
    const host = d.querySelector('.vs-hero .vs-aurora');
    if (!host) return;
    const c = d.createElement('canvas');
    c.className = 'vs-silk';
    const gl = c.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power' });
    if (!gl) return;
    const vs = 'attribute vec2 p;varying vec2 vUv;void main(){vUv=p*.5+.5;gl_Position=vec4(p,0.,1.);}';
    const fs = 'precision mediump float;varying vec2 vUv;uniform float uTime;uniform vec3 uColor;uniform float uSpeed,uScale,uRotation,uNoise;' +
      'const float e=2.71828182845904523536;float noise(vec2 t){vec2 r=e*sin(e*t);return fract(r.x*r.y*(1.+t.x));}' +
      'vec2 rot(vec2 uv,float a){float c=cos(a),s=sin(a);return mat2(c,-s,s,c)*uv;}' +
      'void main(){float rnd=noise(gl_FragCoord.xy);vec2 uv=rot(vUv*uScale,uRotation);vec2 tex=uv*uScale;float o=uSpeed*uTime;' +
      'tex.y+=.03*sin(8.*tex.x-o);float pat=.6+.4*sin(5.*(tex.x+tex.y+cos(3.*tex.x+5.*tex.y)+.02*o)+sin(20.*(tex.x+tex.y-.1*o)));' +
      'vec3 col=uColor*pat-vec3(rnd/15.*uNoise);gl_FragColor=vec4(clamp(col,0.,1.),1.);}';
    const sh = (t, s) => { const o = gl.createShader(t); gl.shaderSource(o, s); gl.compileShader(o); return o; };
    const pr = gl.createProgram();
    gl.attachShader(pr, sh(gl.VERTEX_SHADER, vs)); gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(pr);
    if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) return;
    gl.useProgram(pr);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(pr, 'p');
    gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const u = (n) => gl.getUniformLocation(pr, n);
    gl.uniform3f(u('uColor'), 0.105, 0.43, 0.28);
    gl.uniform1f(u('uSpeed'), 5); gl.uniform1f(u('uScale'), 1); gl.uniform1f(u('uRotation'), 0.42); gl.uniform1f(u('uNoise'), 1.5);
    const uT = u('uTime');
    const q = small ? 0.4 : 0.6;
    const size = () => { c.width = Math.max(2, host.clientWidth * q | 0); c.height = Math.max(2, host.clientHeight * q | 0); gl.viewport(0, 0, c.width, c.height); };
    host.prepend(c); host.classList.add('has-silk'); size();
    addEventListener('resize', size, { passive: true });
    let t = 0, last = 0, raf = 0, vis = true;
    const frame = (now) => {
      raf = 0;
      if (!vis || d.hidden) return;
      const dt = Math.min(0.05, (now - (last || now)) / 1000); last = now;
      t += 0.1 * dt; gl.uniform1f(uT, t); gl.drawArrays(gl.TRIANGLES, 0, 3);
      raf = requestAnimationFrame(frame);
    };
    const go = () => { if (!raf) { last = 0; raf = requestAnimationFrame(frame); } };
    onView(host, (v) => { vis = v; if (v) go(); });
    d.addEventListener('visibilitychange', go);
    gl.uniform1f(uT, 0); gl.drawArrays(gl.TRIANGLES, 0, 3);
    requestAnimationFrame(() => host.classList.add('is-on'));
  }

  /* ---------- ScrollVelocity (ticker speed + direction follow the scroll) ---------- */
  function velocity() {
    if (small || !fine) return;
    d.querySelectorAll('.vs-ticker__track').forEach((track) => {
      const g = track.querySelector('.vs-ticker__group');
      if (!g) return;
      track.classList.add('is-js');
      let w = g.offsetWidth, x = 0, dir = -1, vel = 0, lastY = scrollY, lastT = performance.now(), vis = true, raf = 0;
      addEventListener('resize', () => { w = g.offsetWidth; }, { passive: true });
      const base = small ? 34 : 52; // px per second
      const tick = (now) => {
        raf = 0; if (!vis || d.hidden) return;
        const dt = Math.min(0.05, (now - lastT) / 1000); lastT = now;
        const raw = (scrollY - lastY) / Math.max(dt, 0.001); lastY = scrollY;
        vel = lerp(vel, raw, 0.12);
        const f = Math.min(6, Math.abs(vel) / 300);
        if (vel < -40) dir = 1; else if (vel > 40) dir = -1;
        x += dir * base * (1 + f) * dt;
        if (w) x = ((x % w) - w) % w;
        const skew = Math.max(-8, Math.min(8, vel / -220));
        track.style.transform = `translate3d(${x}px,0,0) skewX(${skew}deg)`;
        raf = requestAnimationFrame(tick);
      };
      d.addEventListener('visibilitychange', () => { if (!d.hidden && vis && !raf) { lastT = performance.now(); lastY = scrollY; raf = requestAnimationFrame(tick); } });
      onView(track, (v) => { vis = v; if (v && !raf) { lastT = performance.now(); lastY = scrollY; raf = requestAnimationFrame(tick); } });
    });
  }

  /* ---------- BlurText (section headings, word by word) ---------- */
  function blurText() {
    const hs = [...d.querySelectorAll('.vs h2:not([data-no-split]), .vs-xmas h2')].filter((h) => !h.closest('.vs-hero'));
    if (!hs.length || !('IntersectionObserver' in W)) return;
    const split = (node, st) => {
      [...node.childNodes].forEach((n) => {
        if (n.nodeType === 3) {
          const frag = d.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.append(part); return; }
            const s = d.createElement('span'); s.className = 'vs-bw'; s.style.setProperty('--bi', st.i++); s.textContent = part; frag.append(s);
          });
          n.replaceWith(frag);
        } else if (n.nodeType === 1 && !n.classList.contains('vs-i') && n.tagName !== 'svg') split(n, st);
      });
    };
    const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-blur-in'); io.unobserve(e.target); } }), { threshold: 0.2, rootMargin: '0px 0px -6% 0px' });
    hs.forEach((h) => {
      const r = h.getBoundingClientRect();
      if (r.top < innerHeight * 0.9 && r.bottom > 0) return; // already on screen: leave untouched
      split(h, { i: 0 }); h.classList.add('vs-blur'); io.observe(h);
    });
  }

  /* ---------- TiltedCard + GlareHover ---------- */
  function tilt() {
    if (!fine) return;
    d.querySelectorAll('.vs-card, .vs-plan').forEach((el) => {
      const glare = d.createElement('span'); glare.className = 'vs-glare'; glare.setAttribute('aria-hidden', 'true'); el.append(glare);
      let tx = 0, ty = 0, cx = 0, cy = 0, s = 1, ts = 1, raf = 0, on = false;
      const amp = el.classList.contains('vs-plan') ? 6 : 10;
      const loop = () => {
        cx = lerp(cx, tx, 0.14); cy = lerp(cy, ty, 0.14); s = lerp(s, ts, 0.14);
        el.style.transform = `perspective(900px) rotateX(${cy.toFixed(2)}deg) rotateY(${cx.toFixed(2)}deg) scale(${s.toFixed(4)})`;
        if (!on && Math.abs(cx) < 0.02 && Math.abs(cy) < 0.02 && Math.abs(s - 1) < 0.001) { el.style.transform = ''; el.classList.remove('is-tilt'); raf = 0; return; }
        raf = requestAnimationFrame(loop);
      };
      el.addEventListener('pointerenter', () => { on = true; ts = 1.025; el.classList.add('is-tilt'); if (!raf) raf = requestAnimationFrame(loop); });
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect(), px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
        tx = (px - 0.5) * amp * 2; ty = (0.5 - py) * amp * 2;
        el.style.setProperty('--gx', (px * 100).toFixed(1) + '%'); el.style.setProperty('--gy', (py * 100).toFixed(1) + '%');
      });
      el.addEventListener('pointerleave', () => { on = false; tx = ty = 0; ts = 1; if (!raf) raf = requestAnimationFrame(loop); });
    });
  }

  /* ---------- SpotlightCard on the remaining surfaces ---------- */
  function spotlight() {
    if (!fine) return;
    d.querySelectorAll('.vs-plan, .vs-hub__item, .vs-step, .vs-faq details, .vs-xmas__card').forEach((el) => {
      if (el.classList.contains('vs-glow')) return;
      el.classList.add('vs-spot-hover');
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', (e.clientX - r.left) + 'px'); el.style.setProperty('--my', (e.clientY - r.top) + 'px');
      });
    });
  }

  /* ---------- Magnet on the big calls to action ---------- */
  function magnet() {
    if (!fine) return;
    d.querySelectorAll('.vs-btn--lg, .vs-xmas__code button').forEach((el) => {
      const pad = 70, k = 4;
      let raf = 0, tx = 0, ty = 0, x = 0, y = 0;
      const loop = () => {
        x = lerp(x, tx, 0.18); y = lerp(y, ty, 0.18);
        el.style.translate = `${x.toFixed(2)}px ${y.toFixed(2)}px`;
        if (Math.abs(x - tx) < 0.05 && Math.abs(y - ty) < 0.05) { if (!tx && !ty) el.style.translate = ''; raf = 0; return; }
        raf = requestAnimationFrame(loop);
      };
      d.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        const inside = e.clientX > r.left - pad && e.clientX < r.right + pad && e.clientY > r.top - pad && e.clientY < r.bottom + pad;
        const nx = inside ? (e.clientX - (r.left + r.width / 2)) / k : 0, ny = inside ? (e.clientY - (r.top + r.height / 2)) / k : 0;
        if (nx !== tx || ny !== ty) { tx = Math.max(-18, Math.min(18, nx)); ty = Math.max(-12, Math.min(12, ny)); if (!raf) raf = requestAnimationFrame(loop); }
      }, { passive: true });
    });
  }

  /* ---------- StarBorder on the primary buy buttons ---------- */
  function star() {
    d.querySelectorAll('[data-vp-main-cta], .vs-final .vs-btn--lg:not(.vs-btn--ghost), .vs-xmas__row .vs-btn').forEach((el) => {
      if (el.querySelector('.vs-star')) return;
      el.classList.add('vs-starred');
      el.insertAdjacentHTML('afterbegin', '<span class="vs-star vs-star--b" aria-hidden="true"></span><span class="vs-star vs-star--t" aria-hidden="true"></span>');
    });
  }

  /* ---------- ClickSpark (one shared canvas) ---------- */
  function spark() {
    if (small || !fine) return;
    const c = d.createElement('canvas'); c.className = 'vs-sparks'; c.setAttribute('aria-hidden', 'true');
    d.body.append(c);
    const ctx = c.getContext('2d'); if (!ctx) return;
    const dpr = Math.min(2, devicePixelRatio || 1);
    const size = () => { c.width = innerWidth * dpr; c.height = innerHeight * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); };
    size(); addEventListener('resize', size, { passive: true });
    let sparks = [], raf = 0;
    const DUR = 460, R = 26, L = 11, N = 9;
    const draw = (now) => {
      ctx.clearRect(0, 0, innerWidth, innerHeight);
      sparks = sparks.filter((s) => {
        const p = (now - s.t) / DUR; if (p >= 1) return false;
        const e = p * (2 - p), dist = e * R, len = L * (1 - e);
        ctx.strokeStyle = s.c; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.beginPath();
        ctx.moveTo(s.x + dist * Math.cos(s.a), s.y + dist * Math.sin(s.a));
        ctx.lineTo(s.x + (dist + len) * Math.cos(s.a), s.y + (dist + len) * Math.sin(s.a)); ctx.stroke();
        return true;
      });
      raf = sparks.length ? requestAnimationFrame(draw) : 0;
    };
    d.addEventListener('pointerdown', (e) => {
      const t = e.target.closest('a, button, summary, [data-copy]'); if (!t) return;
      const col = t.closest('.vs-btn:not(.vs-btn--ghost), .vs-xmas') ? '#efe7d4' : '#4be39a';
      const now = performance.now();
      for (let i = 0; i < N; i++) sparks.push({ x: e.clientX, y: e.clientY, a: (2 * Math.PI * i) / N, t: now, c: col });
      if (!raf) raf = requestAnimationFrame(draw);
    }, { passive: true });
  }

  const run = () => { [star, silk, velocity, blurText, tilt, spotlight, magnet, spark].forEach((f) => { try { f(); } catch (e) {} }); d.documentElement.classList.add('vs-motion'); };
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', run); else run();
})();
